import GoogleMapPin from "@/components/google-map";
import type { LatLng } from "@/components/google-map";
import type { IGeneral } from "@/models/General";
import { Box, Grid, Select, Skeleton, Text, Textarea, TextInput } from "@dehub/theme";
import type { UseFormReturnType } from "@dehub/theme";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import classes from "../../PartnerPage.module.css";
import { DefinitionList } from "../DefinitionList";
import { DEFAULT_MAP_CENTER, parseCoordinate, type IFormData } from "./org-form-model";

type Props = {
  form: UseFormReturnType<IFormData>;
  isEdit: boolean;
};

type IZipCode = { code: string; name: string; parent: string };

const MAP_ZOOM = 16;

export function MainInfoAddressSection({ form, isEdit }: Props) {
  const { t } = useTranslation("partner");
  const { t: tCommon } = useTranslation("common");
  const zipCodes = useSelector((state: { general: IGeneral }) => state.general.zipCodes) as IZipCode[];
  const values = form.values;

  const provinceOptions = useMemo(
    () => zipCodes?.filter((z) => z.parent === "0").map((z) => ({ label: z.name, value: z.code })) ?? [],
    [zipCodes],
  );
  const districtOptions = useMemo(
    () => zipCodes?.filter((z) => z.parent === values.province).map((z) => ({ label: z.name, value: z.code })) ?? [],
    [zipCodes, values.province],
  );
  const khorooOptions = useMemo(
    () => zipCodes?.filter((z) => z.parent === values.district).map((z) => ({ label: z.name, value: z.code })) ?? [],
    [zipCodes, values.district],
  );

  const lat = parseCoordinate(values.locationLat);
  const lng = parseCoordinate(values.locationLng);
  const hasLocation = lat !== null && lng !== null;
  const center: LatLng = hasLocation ? { lat, lng } : DEFAULT_MAP_CENTER;

  const setLocation = (position: LatLng) => {
    form.setFieldValue("locationLat", position.lat.toString());
    form.setFieldValue("locationLng", position.lng.toString());
  };

  if (!isEdit) {
    const zipName = (code: string) => (code ? (zipCodes?.find((z) => z.code === code)?.name ?? code) : "");
    const fenceDoor = [values.khashaaDavkhar, values.khaalgaDugaar].filter(Boolean).join(" · ");
    return (
      <DefinitionList
        items={[
          { key: "province", label: tCommon("address.province"), value: zipName(values.province) },
          { key: "district", label: tCommon("address.district"), value: zipName(values.district) },
          { key: "khoroo", label: tCommon("address.khoroo"), value: zipName(values.khoroo) },
          { key: "khoroolol", label: tCommon("address.street"), value: values.khoroolol },
          { key: "khotkhonBair", label: tCommon("address.building"), value: values.khotkhonBair },
          { key: "fenceDoor", label: t("fields.fenceDoor"), value: fenceDoor },
          { key: "businessAddress", label: t("fields.addressDescription"), value: values.businessAddress, wide: true },
          {
            key: "location",
            label: t("fields.location"),
            value: hasLocation ? `${lat}, ${lng}` : t("noLocation"),
            wide: true,
          },
        ]}
      />
    );
  }

  return (
    <Box className={classes.editBody}>
      <Grid gap="sm">
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Select
            data-path="province"
            data={provinceOptions}
            label={tCommon("address.province")}
            placeholder={tCommon("select")}
            required
            value={values.province || null}
            error={form.errors.province as string | undefined}
            onChange={(v) => {
              form.setFieldValue("province", v ?? "");
              form.setFieldValue("district", "");
              form.setFieldValue("khoroo", "");
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Select
            data-path="district"
            data={districtOptions}
            disabled={!values.province}
            label={tCommon("address.district")}
            placeholder={tCommon("select")}
            required
            value={values.district || null}
            error={form.errors.district as string | undefined}
            onChange={(v) => {
              form.setFieldValue("district", v ?? "");
              form.setFieldValue("khoroo", "");
            }}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
          <Select
            data={khorooOptions}
            disabled={!values.district}
            label={tCommon("address.khoroo")}
            placeholder={tCommon("select")}
            required
            {...form.getInputProps("khoroo")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <TextInput
            label={tCommon("address.street")}
            required
            {...form.getInputProps("khoroolol")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <TextInput
            label={tCommon("address.building")}
            required
            {...form.getInputProps("khotkhonBair")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <TextInput
            label={tCommon("address.fence")}
            required
            {...form.getInputProps("khashaaDavkhar")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, md: 3 }}>
          <TextInput
            label={tCommon("address.door")}
            required
            {...form.getInputProps("khaalgaDugaar")}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Textarea
            label={t("fields.addressDescription")}
            autosize
            minRows={2}
            required
            {...form.getInputProps("businessAddress")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Box className={classes.mapBox}>
            <Skeleton className={classes.mapSkeleton} aria-hidden="true" />
            <Box className={classes.mapLayer}>
              <GoogleMapPin center={center} zoom={MAP_ZOOM} hideMarker={!hasLocation} onChange={setLocation} />
            </Box>
          </Box>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Box className={classes.mono}>
            <TextInput label={t("fields.lat")} {...form.getInputProps("locationLat")} />
            <TextInput mt="sm" label={t("fields.lng")} {...form.getInputProps("locationLng")} />
          </Box>
          <Text mt="sm" className={classes.mapNote}>
            {hasLocation ? t("mapCaption") : `${t("locationNotSet")}. ${t("mapCaption")}`}
          </Text>
        </Grid.Col>
      </Grid>
    </Box>
  );
}
